import { actions } from "../../app/state.js";
import { EXPENSE_CATEGORIES } from "../../domain/finance.js";
import {
  deleteEventExpense,
  getReceiptSignedUrl,
  listExpensesForEvents,
  sumExpenses,
  updateEventExpenseReceipt,
  uploadEventReceipt,
} from "../../services/expensesService.js";
import { createPayment, deletePayment, listPaymentsByEvent, getDepositStatus } from "../../services/paymentsService.js";
import { getCurrentTeamId } from "../../services/teamService.js";
import { listLineupMembers } from "../../services/lineupService.js";
import { $, escapeHtml } from "./EventModal.utils.js";
import ExpenseModal from "./ExpenseModal.js";
import * as XLSX from "xlsx";

const { openCreateEventExpenseModal } = ExpenseModal;

export async function wireFinanceLogic(root, eventId, teamId, eventData, { setEditMode } = {}) {
  if (!eventId) return;

  const applyEditMode = () => {
    setEditMode?.(root, root.getAttribute("data-is-editable") === "true");
  };

  const paymentsList = $(root, "#event-finance-payments-list");

  async function loadPayments() {
    if (!paymentsList) return;
    paymentsList.innerHTML = '<div class="text-center p-2"><i class="fa-solid fa-spinner fa-spin text-slate-400"></i></div>';

    try {
      const payments = await listPaymentsByEvent(eventId);
      if (payments.length === 0) {
        paymentsList.innerHTML = '<div class="text-sm text-slate-400 italic p-2 text-center">No hay pagos registrados</div>';
        return;
      }

      paymentsList.innerHTML = payments.map(p => `
        <div class="flex justify-between items-center text-sm p-2 bg-slate-50 rounded-lg mb-1">
          <div>
            <div class="font-medium text-slate-700">${escapeHtml(p.note || (p.payment_type === "advance" ? "Señal" : "Pago"))}</div>
            <div class="text-xs text-slate-500">${new Date(p.payment_date || new Date()).toLocaleDateString("es-ES")}</div>
          </div>
          <div class="flex items-center gap-3">
            <span class="font-bold text-slate-900">${Number(p.amount).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</span>
            <button class="text-red-400 hover:text-red-600 delete-payment-btn" data-id="${p.id}">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      `).join("");

      paymentsList.querySelectorAll(".delete-payment-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
          if (!confirm("¿Eliminar pago?")) return;
          try {
            await deletePayment(btn.dataset.id);
            loadPayments();
            actions.addToast?.({ type: "success", message: "Pago eliminado" });
          } catch (err) {
            console.error(err);
            actions.addToast?.({ type: "error", message: "Error eliminando pago" });
          }
        });
      });

      applyEditMode();
      if (root.dataset.viewerExpenseOnly === "true") applyViewerExpenseMode(root);
    } catch (e) {
      console.error(e);
      paymentsList.innerHTML = '<div class="text-red-400 text-xs text-center">Error cargando pagos</div>';
    }
  }

  $(root, "#event-payment-add")?.addEventListener("click", () => {
    const amountStr = prompt("Importe del pago:");
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (Number.isNaN(amount)) return alert("Importe inválido");

    const note = prompt("Nota (ej: Señal, Pago Final):", "Pago");

    createPayment({
      event_id: eventId,
      team_id: teamId,
      amount,
      note,
      payment_date: new Date().toISOString().split("T")[0],
      payment_type: note.toLowerCase().includes("señal") ? "advance" : "final",
    })
      .then(async () => {
        loadPayments();
        actions.addToast?.({ type: "success", message: "Pago registrado" });
        
        // Sugerencia de pasar a Confirmado si estaba en accepted_pending_deposit
        // y la señal registrada ya cubre la pactada
        if (eventData && eventData.stage === "accepted_pending_deposit" && note.toLowerCase().includes("señal")
          && (await getDepositStatus(eventData)).covered) {
          actions.addToast?.({ type: "info", message: "Señal registrada. El evento ya puede ser Confirmado." });
          const stageSel = $(root, "#event-stage");
          if (stageSel) {
            stageSel.value = "confirmed";
            // Flash el select para llamar la atención
            stageSel.classList.add("ring-2", "ring-emerald-500");
            setTimeout(() => stageSel.classList.remove("ring-2", "ring-emerald-500"), 2000);
          }
        }
      })
      .catch(err => actions.addToast?.({ type: "error", message: err.message }));
  });

  loadPayments();

  const catSelect = $(root, "#finance-exp-cat");
  const subcatSelect = $(root, "#finance-exp-subcat");
  const ownerSelect = $(root, "#finance-exp-owner");
  const memberWrapper = $(root, "#finance-exp-member-wrapper");
  const memberSelect = $(root, "#finance-exp-member");
  const reimbursableWrapper = $(root, "#finance-exp-reimbursable-wrapper");

  ownerSelect?.addEventListener("change", () => {
    const isPersonal = ownerSelect.value === "personal";
    if (isPersonal) {
      memberWrapper?.classList.remove("opacity-30", "select-none", "pointer-events-none");
      reimbursableWrapper?.classList.remove("opacity-30", "select-none", "pointer-events-none");
    } else {
      memberWrapper?.classList.add("opacity-30", "select-none", "pointer-events-none");
      reimbursableWrapper?.classList.add("opacity-30", "select-none", "pointer-events-none");
      if (memberSelect) memberSelect.value = "";
    }
  });

  if (memberSelect) {
    try {
      const myTeamId = teamId || await getCurrentTeamId();
      const members = await listLineupMembers(myTeamId);
      memberSelect.innerHTML = '<option value="">— Miembro —</option>';
      members.forEach(m => {
        const opt = document.createElement("option");
        opt.value = m.id;
        opt.textContent = m.display_name;
        memberSelect.appendChild(opt);
      });
    } catch (e) {
      console.warn("Error loading team members for expense form:", e);
    }
  }

  if (catSelect) {
    catSelect.innerHTML = '<option value="">—</option>' + EXPENSE_CATEGORIES
      .map(c => `<option value="${c.expense_category}">${c.expense_category}</option>`)
      .filter((v, i, a) => a.indexOf(v) === i)
      .join("");

    catSelect.addEventListener("change", () => {
      const val = catSelect.value;
      const subs = EXPENSE_CATEGORIES
        .filter(c => c.expense_category === val && c.expense_subcategory)
        .map(c => c.expense_subcategory);

      subcatSelect.innerHTML = '<option value="">—</option>';
      if (subs.length > 0) {
        subcatSelect.disabled = false;
        subcatSelect.innerHTML += subs.map(s => `<option value="${s}">${s}</option>`).join("");
      } else {
        subcatSelect.disabled = true;
      }
    });
  }

  $(root, "#finance-exp-add-btn")?.addEventListener("click", () => {
    openCreateEventExpenseModal({
      event_id: eventId,
      defaultOwner: root.dataset.viewerExpenseOnly === "true" ? "personal" : null,
      lockOwner: root.dataset.viewerExpenseOnly === "true",
      onSuccess: loadExpenses,
    });
  });

  const expList = $(root, "#event-finance-expenses-list");
  const totalLabel = $(root, "#event-finance-total-expenses");
  let currentExpenses = [];

  async function loadExpenses() {
    if (!expList) return;
    expList.innerHTML = '<div class="text-center p-2"><i class="fa-solid fa-spinner fa-spin text-slate-400"></i></div>';

    try {
      const expenses = await listExpensesForEvents([eventId]);
      currentExpenses = expenses;

      const total = sumExpenses(expenses);
      totalLabel.textContent = total.toLocaleString("es-ES", { style: "currency", currency: "EUR" });

      if (expenses.length === 0) {
        expList.innerHTML = '<div class="text-sm text-slate-400 text-center py-4">No hay gastos registrados.</div>';
        return;
      }

      expList.innerHTML = expenses.map(ex => {
        const ownerBadge = ex.expense_owner === "personal"
          ? '<span class="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">Personal</span>'
          : '<span class="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">Empresa</span>';
        const reimbBadge = ex.is_reimbursable
          ? '<span class="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold">Reemb.</span>'
          : "";

        return `
          <div class="flex justify-between items-center p-2 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors bg-white rounded-lg mb-1">
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                ${ownerBadge}
                <span class="font-semibold text-slate-700 text-sm">${escapeHtml(ex.expense_category)}</span>
                ${ex.expense_subcategory ? `<span class="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">${escapeHtml(ex.expense_subcategory)}</span>` : ""}
                ${reimbBadge}
              </div>
              ${ex.note ? `<div class="text-xs text-slate-400 mt-0.5">${escapeHtml(ex.note)}</div>` : ""}
              <div class="mt-1 flex flex-col items-start gap-1">
                ${ex.receipt_url ? `<button type="button" class="text-[10px] text-blue-600 hover:text-blue-800 font-bold btn-view-event-receipt" data-receipt-path="${ex.receipt_url}"><i class="fa-solid fa-receipt mr-1"></i>Ver ticket</button>` : ""}
                <label class="text-[10px] text-slate-500 font-bold">
                  ${ex.receipt_url ? "Cambiar archivo/foto" : "Adjuntar archivo/foto"}
                  <input type="file" class="block max-w-[160px] text-[10px] font-normal event-expense-receipt-input" data-exp-id="${ex.id}" accept="image/*,.jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,application/pdf">
                </label>
                ${ex.receipt_url ? `<button type="button" class="text-[10px] text-red-500 hover:text-red-700 font-bold btn-remove-event-receipt" data-exp-id="${ex.id}"><i class="fa-solid fa-xmark mr-1"></i>Quitar archivo</button>` : ""}
              </div>
            </div>
            <div class="flex items-center gap-3">
              <span class="font-bold text-slate-900 text-sm">${Number(ex.amount).toLocaleString("es-ES", { minimumFractionDigits: 2 })}€</span>
              <button class="text-slate-300 hover:text-red-500 delete-expense-btn transition-colors" data-id="${ex.id}">
                <i class="fa-solid fa-trash text-xs"></i>
              </button>
            </div>
          </div>
        `;
      }).join("");

      expList.querySelectorAll(".delete-expense-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
          if (!confirm("¿Borrar gasto?")) return;
          try {
            await deleteEventExpense(btn.dataset.id);
            loadExpenses();
          } catch (e) {
            actions.addToast?.({ type: "error", message: "Error borrando gasto" });
          }
        });
      });

      expList.querySelectorAll(".btn-view-event-receipt").forEach(btn => {
        btn.addEventListener("click", async () => {
          const path = btn.dataset.receiptPath;
          if (!path) return;
          try {
            btn.disabled = true;
            const url = await getReceiptSignedUrl(path);
            if (url) window.open(url, "_blank");
            else actions.addToast?.({ type: "error", message: "No se pudo abrir el ticket" });
          } catch (e) {
            console.error(e);
            actions.addToast?.({ type: "error", message: "Error al abrir el ticket" });
          } finally {
            btn.disabled = false;
          }
        });
      });

      expList.querySelectorAll(".event-expense-receipt-input").forEach(input => {
        input.addEventListener("change", async () => {
          const file = input.files?.[0];
          const expenseId = input.dataset.expId;
          if (!file || !expenseId) return;

          try {
            input.disabled = true;
            actions.addToast?.({ type: "info", message: "Subiendo archivo..." });
            const path = await uploadEventReceipt(file);
            await updateEventExpenseReceipt(expenseId, path);
            actions.addToast?.({ type: "success", message: "Archivo adjuntado correctamente" });
            await loadExpenses();
          } catch (e) {
            console.error(e);
            actions.addToast?.({ type: "error", message: "Error adjuntando archivo" });
          } finally {
            input.disabled = false;
            input.value = "";
          }
        });
      });

      expList.querySelectorAll(".btn-remove-event-receipt").forEach(btn => {
        btn.addEventListener("click", async () => {
          const expenseId = btn.dataset.expId;
          if (!expenseId) return;
          if (!confirm("¿Quitar el archivo adjunto de este gasto?")) return;

          try {
            btn.disabled = true;
            await updateEventExpenseReceipt(expenseId, null);
            actions.addToast?.({ type: "success", message: "Archivo quitado del gasto" });
            await loadExpenses();
          } catch (e) {
            console.error(e);
            actions.addToast?.({ type: "error", message: "Error quitando archivo" });
          } finally {
            btn.disabled = false;
          }
        });
      });

      applyEditMode();
      if (root.dataset.viewerExpenseOnly === "true") applyViewerExpenseMode(root);
    } catch (e) {
      console.error(e);
      expList.innerHTML = '<div class="text-red-400 text-xs">Error cargando gastos</div>';
    }
  }

  loadExpenses();

  $(root, "#event-finance-export-excel")?.addEventListener("click", () => {
    if (currentExpenses.length === 0) {
      return actions.addToast?.({ type: "info", message: "No hay gastos para exportar" });
    }

    try {
      const data = currentExpenses.map(e => ({
        Fecha: new Date(e.created_at).toLocaleDateString(),
        Tipo: e.expense_owner === "personal" ? "Personal" : "Empresa",
        Categoría: e.expense_category,
        Subcategoría: e.expense_subcategory || "",
        Nota: e.note || "",
        Importe: Number(e.amount),
        Reembolsable: e.is_reimbursable ? "Sí" : "No",
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Gastos");
      XLSX.writeFile(wb, `Gastos_${(eventData.client_name_legacy || "Evento").replace(/[^a-z0-9]/gi, "_")}.xlsx`);

      actions.addToast?.({ type: "success", message: "Excel descargado" });
    } catch (e) {
      console.error(e);
      actions.addToast?.({ type: "error", message: "Error exportando Excel" });
    }
  });
}

export function applyViewerExpenseMode(root) {
  $(root, "#event-finance-payments-list")?.closest("section")?.classList.add("hidden");
  $(root, "#event-finance-export-excel")?.classList.add("hidden");
  $(root, "#event-finance-expenses-list")?.classList.add("hidden");
  $(root, "#event-finance-total-expenses")?.closest(".mt-6")?.classList.add("hidden");

  [
    "#finance-exp-owner",
    "#finance-exp-member",
    "#finance-exp-cat",
    "#finance-exp-subcat",
    "#finance-exp-amount",
    "#finance-exp-note",
  ].forEach(sel => {
    const el = $(root, sel);
    if (!el) return;
    el.disabled = false;
    el.classList.remove("bg-slate-50", "cursor-not-allowed");
  });

  const expAddBtn = $(root, "#finance-exp-add-btn");
  if (expAddBtn) {
    expAddBtn.disabled = false;
    expAddBtn.classList.remove("hidden");
  }
}
